import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Reveal } from "../../lib/motion";

const CHAPTERS = [
  {
    n: "01",
    title: "The world doesn’t need more memorisers.",
    body:
      "The world of work has shifted. What matters now is the ability to imagine, decide, adapt and create — not just to recall facts.",
    color: "#FFD700",
  },
  {
    n: "02",
    title: "Content isn’t the curriculum any more.",
    body:
      "The real curriculum is capability: confidence, creativity, collaboration and problem-solving. Content becomes the raw material, not the destination.",
    color: "#FF007F",
  },
  {
    n: "03",
    title: "Tech alone won’t shift a school.",
    body:
      "New devices in a cupboard don’t change learning. A shared framework, confident educators and a working pathway do.",
    color: "#0055FF",
  },
  {
    n: "04",
    title: "Makerspaces should feel loud, alive, useful.",
    body:
      "Not showpieces. Not compliance rooms. Working studios where students build, test, ship and share things that matter to them.",
    color: "#00C2B8",
  },
  {
    n: "05",
    title: "The shift is a practice — not a project.",
    body:
      "It doesn’t happen in a one-day PD or a launch event. It happens in weekly rhythms, small experiments and a team willing to try, learn and try again.",
    color: "#FF6600",
  },
];

export default function Manifesto() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const bigNumX = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  return (
    <section
      ref={ref}
      data-testid="manifesto-section"
      className="relative bg-[#1A1A1A] text-white overflow-hidden border-y-2 border-[#1A1A1A]"
    >
      <div className="absolute inset-0 bg-dots-light opacity-15 pointer-events-none" />
      {/* Giant kinetic number */}
      <motion.div
        style={{ x: bigNumX }}
        aria-hidden
        className="pointer-events-none select-none absolute -top-20 left-0 right-0 font-display font-black text-[38vw] leading-[0.8] tracking-tighter text-white/[0.04] whitespace-nowrap"
      >
        MANIFESTO · MANIFESTO ·
      </motion.div>

      <div className="relative mx-auto max-w-7xl px-5 md:px-10 lg:px-12 py-24 md:py-32">
        <Reveal>
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-[#FFD700]">
            Manifesto — in 5 chapters
          </p>
          <h2 className="mt-4 font-display font-black text-4xl sm:text-5xl lg:text-7xl leading-[0.98] tracking-tighter max-w-4xl">
            The way we prepare young people has to{" "}
            <span className="italic text-[#00C2B8]">catch up</span> — fast.
          </h2>
        </Reveal>

        <div className="mt-16 md:mt-24 space-y-16 md:space-y-24">
          {CHAPTERS.map((c, i) => (
            <ChapterRow key={c.n} chapter={c} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ChapterRow({ chapter, index }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <motion.div
      ref={ref}
      data-testid={`manifesto-chapter-${chapter.n}`}
      className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12 items-start"
    >
      <motion.div
        style={{ y }}
        className="md:col-span-3 flex md:block items-baseline gap-4"
      >
        <span
          className="font-display font-black text-[6rem] md:text-[9rem] leading-[0.8] tracking-tighter"
          style={{ color: chapter.color }}
        >
          {chapter.n}
        </span>
        <span
          className="hidden md:block mt-2 h-1.5 rounded-full"
          style={{ backgroundColor: chapter.color, width: "60%" }}
        />
      </motion.div>
      <div className="md:col-span-9 max-w-3xl">
        <motion.h3
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="font-display font-black text-3xl md:text-5xl leading-[1.02] tracking-tighter"
        >
          {chapter.title}
        </motion.h3>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="mt-5 text-lg md:text-xl text-white/70 font-medium leading-relaxed"
        >
          {chapter.body}
        </motion.p>
      </div>
    </motion.div>
  );
}
